import { Action, Actions, ofActionDispatched, Selector, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ChecklistTemplateDetailXsState } from '../checklist-template/checklist-template-detail-xs.state';

import {
  ChecklistTemplateEntryConvertToTableEntryAction
} from './checklist-template-entry-table-row-conversion.action';
import { ResetStateDefaults } from '../base-action-class.model';
import { ChecklistTemplateEntry } from '../../models/checklist-template-entry/checklist-template-entry.model';
import { ChecklistTemplateEntryRequestService } from '../../services/request/checklist-template-entry-request.service';
import { map, takeUntil } from 'rxjs/operators';
import {
  ChecklistTemplateEntryConditionAction
} from '../checklist-template-entry-condition/checklist-template-entry-condition.action';

interface ChecklistTemplateEntryConvertToTableEntryStateModel {
  toTableRowActive: boolean;
  toTableRowTargetEntryId: string;
  availableEntriesToTableRow: ChecklistTemplateEntry[];

  toEntryActive: boolean;
  toEntryTargetEntryId: string;
  availableEntriesToEntry: ChecklistTemplateEntry[];
}

const getDefaults = (): ChecklistTemplateEntryConvertToTableEntryStateModel => ({
  toTableRowActive: false,
  toTableRowTargetEntryId: null,
  availableEntriesToTableRow: [],

  toEntryActive: false,
  toEntryTargetEntryId: null,
  availableEntriesToEntry: []
});

@State<ChecklistTemplateEntryConvertToTableEntryStateModel>({
  name: 'checklistTemplateEntryConvertToTable',
  defaults: getDefaults()
})
@Injectable()
export class ChecklistTemplateEntryTableRowConversionXsState {


  //region INJECTIONS
  private store = inject(Store);
  private actions = inject(Actions);
  private checklistTemplateEntryRequestService = inject(ChecklistTemplateEntryRequestService);

  //endregion

  @Selector()
  static toTableRowActive(state: ChecklistTemplateEntryConvertToTableEntryStateModel): boolean {
    return state.toTableRowActive;
  }

  @Selector()
  static availableEntriesToTableRow(state: ChecklistTemplateEntryConvertToTableEntryStateModel): ChecklistTemplateEntry[] {
    return state.availableEntriesToTableRow;
  }

  @Selector()
  static toTableRowTargetEntryId(state: ChecklistTemplateEntryConvertToTableEntryStateModel): string {
    return state.toTableRowTargetEntryId;
  }


  @Selector()
  static toEntryActive(state: ChecklistTemplateEntryConvertToTableEntryStateModel): boolean {
    return state.toEntryActive;
  }

  @Selector()
  static availableEntriesToEntry(state: ChecklistTemplateEntryConvertToTableEntryStateModel): ChecklistTemplateEntry[] {
    return state.availableEntriesToEntry;
  }

  @Selector()
  static toEntryTargetEntryId(state: ChecklistTemplateEntryConvertToTableEntryStateModel): string {
    return state.toEntryTargetEntryId;
  }

  @Action(ChecklistTemplateEntryConvertToTableEntryAction.ToTableRowInit)
  public toTableRowInit(
    ctx: StateContext<ChecklistTemplateEntryConvertToTableEntryStateModel>,
    action: ChecklistTemplateEntryConvertToTableEntryAction.ToTableRowInit
  ): Observable<void> {
    const state: ChecklistTemplateEntryConvertToTableEntryStateModel = ctx.getState();

    if (state.toTableRowActive) {
      return throwError(() => new Error('State is already active!'));
    }

    if (!action.targetEntryId) {
      return throwError(() => new Error('Checklist template entry id cannot be null!'));
    }

    const checklistTemplateId: string = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (!checklistTemplateId) {
      return throwError(() => new Error('Checklist template detail page is not initialized!'));
    }

    ctx.patchState({
      toTableRowActive: true,
      toTableRowTargetEntryId: action.targetEntryId
    });

    return this.checklistTemplateEntryRequestService.getChecklistTemplateEntriesAvailableForConversion(
      checklistTemplateId,
      action.targetEntryId
    )
      .pipe(
        takeUntil(this.actions.pipe(
          ofActionDispatched(ChecklistTemplateEntryConditionAction.ResetState)
        )),
        map((possibleEntries: ChecklistTemplateEntry[]) => {
          ctx.patchState({ availableEntriesToTableRow: possibleEntries });
        })
      );
  }

  @Action(ChecklistTemplateEntryConvertToTableEntryAction.ConvertToTableEntry)
  public convertToTableEntry(
    ctx: StateContext<ChecklistTemplateEntryConvertToTableEntryStateModel>,
    action: ChecklistTemplateEntryConvertToTableEntryAction.ConvertToTableEntry
  ): Observable<void> {
    const state: ChecklistTemplateEntryConvertToTableEntryStateModel = ctx.getState();

    if (!action?.selectedEntryIds?.length) {
      return throwError(() => new Error('Selected entry ids cannot be empty!'));
    }

    const checklistTemplateId: string = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    return this.checklistTemplateEntryRequestService.convertEntryToTableRow(
      checklistTemplateId,
      state.toTableRowTargetEntryId,
      action.selectedEntryIds
    )
      .pipe(
        takeUntil(this.actions.pipe(
          ofActionDispatched(ChecklistTemplateEntryConvertToTableEntryAction.ConvertToTableReset)
        )),
        map(() => {
          this.toTableRowDefaults(ctx);
          this.store.dispatch(new ChecklistTemplateEntryConvertToTableEntryAction.EntryReFetchNeeded());
        })
      );

  }

  @Action([
    ChecklistTemplateEntryConvertToTableEntryAction.ConvertToTableReset,
    ResetStateDefaults
  ])
  public resetToTableRow(ctx: StateContext<ChecklistTemplateEntryConvertToTableEntryStateModel>): void {
    this.toTableRowDefaults(ctx);
  }


  private toTableRowDefaults(ctx: StateContext<ChecklistTemplateEntryConvertToTableEntryStateModel>) {
    ctx.patchState({
      toTableRowActive: false,
      toTableRowTargetEntryId: null,
      availableEntriesToTableRow: []
    });
  }

  @Action(ChecklistTemplateEntryConvertToTableEntryAction.ToEntryInit)
  public toEntryInit(
    ctx: StateContext<ChecklistTemplateEntryConvertToTableEntryStateModel>,
    action: ChecklistTemplateEntryConvertToTableEntryAction.ToEntryInit
  ): Observable<void> {
    const state: ChecklistTemplateEntryConvertToTableEntryStateModel = ctx.getState();

    if (state.toTableRowActive) {
      return throwError(() => new Error('State is already active!'));
    }

    if (!action.targetEntryId) {
      return throwError(() => new Error('Checklist template entry id cannot be null!'));
    }

    const checklistTemplateId: string = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (!checklistTemplateId) {
      return throwError(() => new Error('Checklist template detail page is not initialized!'));
    }

    ctx.patchState({
      toEntryActive: true,
      toEntryTargetEntryId: action.targetEntryId
    });

    return this.checklistTemplateEntryRequestService.getTableRowsForTable(
      checklistTemplateId,
      action.targetEntryId
    )
      .pipe(
        takeUntil(this.actions.pipe(
          ofActionDispatched(ChecklistTemplateEntryConditionAction.ResetState)
        )),
        map((possibleEntries: ChecklistTemplateEntry[]) => {
          ctx.patchState({ availableEntriesToEntry: possibleEntries });
        })
      );
  }


  @Action(ChecklistTemplateEntryConvertToTableEntryAction.ConvertToEntry)
  public convertTableRowToEntry(
    ctx: StateContext<ChecklistTemplateEntryConvertToTableEntryStateModel>,
    action: ChecklistTemplateEntryConvertToTableEntryAction.ConvertToEntry
  ): Observable<void> {
    const state: ChecklistTemplateEntryConvertToTableEntryStateModel = ctx.getState();

    if (!action?.selectedEntryIds?.length) {
      return throwError(() => new Error('Selected entry ids cannot be empty!'));
    }

    const checklistTemplateId: string = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    return this.checklistTemplateEntryRequestService.convertTableRowToEntry(
      checklistTemplateId,
      state.toEntryTargetEntryId,
      action.selectedEntryIds
    )
      .pipe(
        takeUntil(this.actions.pipe(
          ofActionDispatched(ChecklistTemplateEntryConvertToTableEntryAction.ConvertToEntryReset)
        )),
        map(() => {
          this.toEntryDefaults(ctx);
          this.store.dispatch(new ChecklistTemplateEntryConvertToTableEntryAction.EntryReFetchNeeded());
        })
      );

  }

  @Action([
    ChecklistTemplateEntryConvertToTableEntryAction.ConvertToEntryReset,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<ChecklistTemplateEntryConvertToTableEntryStateModel>): void {
    this.toEntryDefaults(ctx);
  }

  private toEntryDefaults(ctx: StateContext<ChecklistTemplateEntryConvertToTableEntryStateModel>) {
    ctx.patchState({
      toEntryActive: false,
      toEntryTargetEntryId: null,
      availableEntriesToEntry: []
    });
  }
}
